import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatDateTime, type CommitImportResult, type ImportDto, type ImportPageDto } from '@pb/shared';
import { commitImport, discardImport, getImport, type CommitImportInput } from '../api/imports';
import { listGroups } from '../api/groups';
import { TypedConfirmDialog } from '../components/ConfirmDialogs';
import { DataView, ErrorAlert, Loading } from '../components/States';
import { Thumb } from '../components/Thumb';
import { useDetailCrumb, useProject, useProjectOwnerGuard } from '../lib/project';
import { anyPending } from '../lib/thumbs';
import { firstLine, pageCountLabel, pluralize } from '../lib/text';
import { usePolling, useResource } from '../lib/useResource';

interface Unit {
  key: string;
  pages: number[];
  title: string;
}

interface NewGroup {
  key: string;
  name: string;
  unitKeys: string[];
}

interface AppendGroup {
  groupId: number;
  unitKeys: string[];
}

let keySeq = 0;
const nextKey = (prefix: string) => `${prefix}${++keySeq}`;

function defaultTitle(page: ImportPageDto | undefined): string {
  if (!page) return 'Untitled slide';
  return page.title?.trim() || firstLine(page.text, 120) || 'Untitled slide';
}

function sortUnits(units: Unit[]): Unit[] {
  return units
    .map((u) => ({ ...u, pages: [...u.pages].sort((a, b) => a - b) }))
    .sort((a, b) => (a.pages[0] ?? 0) - (b.pages[0] ?? 0));
}

function CommitDone({ result }: { result: CommitImportResult }) {
  const { project } = useProject();
  const base = `/projects/${project.id}`;
  return (
    <div className="stack-lg">
      <div className="alert alert-success" role="status">
        The import was committed: {pluralize(result.slides.length, 'slide')} and {pluralize(result.groups.length, 'group')}{' '}
        changed or created.
      </div>
      <div className="two-col-even">
        <section className="panel">
          <div className="panel-title">
            <h3>Slides</h3>
          </div>
          <ul className="list-plain">
            {result.slides.map((s) => (
              <li key={s.id} className="row-between">
                <Link to={`${base}/slides/${s.id}`}>{s.title}</Link>
                <span className="small faint">{pluralize(s.page_count, 'page')}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="panel">
          <div className="panel-title">
            <h3>Groups</h3>
          </div>
          {result.groups.length === 0 ? (
            <p className="small muted">No groups were created or changed.</p>
          ) : (
            <ul className="list-plain">
              {result.groups.map((g) => (
                <li key={g.id} className="row-between">
                  <Link to={`${base}/groups/${g.id}`}>{g.name}</Link>
                  <span className="small faint">version {g.version_no}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <div className="row">
        <Link className="btn btn-primary" to={`${base}/library`}>
          Go to the library
        </Link>
        <Link className="btn" to={`${base}/groups`}>
          Go to groups
        </Link>
      </div>
    </div>
  );
}

function ImportReview({ data, onRefresh }: { data: ImportDto; onRefresh: () => void }) {
  const { project, tags, reloadTags } = useProject();
  const navigate = useNavigate();
  const groups = useResource(() => listGroups(project.id), [project.id]);
  const pageByNo = useMemo(() => new Map(data.pages.map((p) => [p.page_no, p])), [data.pages]);

  const [units, setUnits] = useState<Unit[]>(() =>
    data.pages.map((p) => ({ key: nextKey('u'), pages: [p.page_no], title: '' })),
  );
  const [skipped, setSkipped] = useState<number[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [newGroups, setNewGroups] = useState<NewGroup[]>([]);
  const [appendGroups, setAppendGroups] = useState<AppendGroup[]>([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [appendTarget, setAppendTarget] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<CommitImportResult | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [tagIds, setTagIds] = useState<number[]>([]);

  const cleanGroups = (keys: Set<string>) => {
    setNewGroups((gs) => gs.map((g) => ({ ...g, unitKeys: g.unitKeys.filter((k) => keys.has(k)) })));
    setAppendGroups((gs) => gs.map((g) => ({ ...g, unitKeys: g.unitKeys.filter((k) => keys.has(k)) })));
    setSelected((s) => s.filter((k) => keys.has(k)));
  };

  const skipPage = (pageNo: number) => {
    const next = units
      .map((u) => ({ ...u, pages: u.pages.filter((p) => p !== pageNo) }))
      .filter((u) => u.pages.length > 0);
    setUnits(next);
    setSkipped((s) => [...s, pageNo]);
    cleanGroups(new Set(next.map((u) => u.key)));
  };

  const restorePage = (pageNo: number) => {
    setSkipped((s) => s.filter((p) => p !== pageNo));
    setUnits((us) => sortUnits([...us, { key: nextKey('u'), pages: [pageNo], title: '' }]));
  };

  const combine = () => {
    const chosen = sortUnits(units.filter((u) => selected.includes(u.key)));
    const first = chosen[0];
    if (chosen.length < 2 || !first) return;
    const merged: Unit = {
      key: first.key,
      pages: chosen.flatMap((u) => u.pages).sort((a, b) => a - b),
      title: chosen.find((u) => u.title.trim())?.title ?? '',
    };
    const next = sortUnits([...units.filter((u) => !selected.includes(u.key)), merged]);
    setUnits(next);
    setSelected([merged.key]);
    cleanGroups(new Set(next.map((u) => u.key)));
  };

  const split = (unit: Unit) => {
    const [firstPage, ...rest] = unit.pages;
    if (firstPage === undefined) return;
    const next = sortUnits([
      ...units.filter((u) => u.key !== unit.key),
      { ...unit, pages: [firstPage] },
      ...rest.map((p) => ({ key: nextKey('u'), pages: [p], title: '' })),
    ]);
    setUnits(next);
  };

  const orderedSelection = () => units.filter((u) => selected.includes(u.key)).map((u) => u.key);

  const addNewGroup = () => {
    const keys = orderedSelection();
    if (!newGroupName.trim() || keys.length === 0) return;
    setNewGroups((gs) => [...gs, { key: nextKey('g'), name: newGroupName.trim(), unitKeys: keys }]);
    setNewGroupName('');
    setSelected([]);
  };

  const addAppend = () => {
    const keys = orderedSelection();
    if (appendTarget === '' || keys.length === 0) return;
    setAppendGroups((gs) => {
      const existing = gs.find((g) => g.groupId === appendTarget);
      if (existing) {
        return gs.map((g) =>
          g.groupId === appendTarget ? { ...g, unitKeys: [...g.unitKeys, ...keys.filter((k) => !g.unitKeys.includes(k))] } : g,
        );
      }
      return [...gs, { groupId: appendTarget, unitKeys: keys }];
    });
    setAppendTarget('');
    setSelected([]);
  };

  const unitLabel = (key: string) => {
    const index = units.findIndex((u) => u.key === key);
    const unit = units[index];
    if (!unit) return key;
    return unit.title.trim() || defaultTitle(pageByNo.get(unit.pages[0] ?? 0));
  };

  const commit = async () => {
    const index = new Map(units.map((u, i) => [u.key, i]));
    const toIdx = (keys: string[]) => keys.map((k) => index.get(k)).filter((i): i is number => i !== undefined);
    const input: CommitImportInput = {
      units: units.map((u) => (u.title.trim() ? { pages: u.pages, title: u.title.trim() } : { pages: u.pages })),
      new_groups: newGroups
        .map((g) => ({ name: g.name, units: toIdx(g.unitKeys) }))
        .filter((g) => g.units.length > 0),
      append_to_groups: appendGroups
        .map((g) => ({ group_id: g.groupId, units: toIdx(g.unitKeys) }))
        .filter((g) => g.units.length > 0),
      tag_ids: tagIds.filter((id) => tags.some((t) => t.id === id)),
    };
    setBusy(true);
    setError(null);
    try {
      setResult(await commitImport(data.id, input));
      if (input.tag_ids?.length) void reloadTags();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (result) return <CommitDone result={result} />;

  const groupNames = new Map((groups.data ?? []).map((g) => [g.id, g.name]));
  const membership = (key: string) => [
    ...newGroups.filter((g) => g.unitKeys.includes(key)).map((g) => `New: ${g.name}`),
    ...appendGroups.filter((g) => g.unitKeys.includes(key)).map((g) => groupNames.get(g.groupId) ?? `Group ${g.groupId}`),
  ];

  type Entry = { kind: 'unit'; unit: Unit; at: number } | { kind: 'skipped'; pageNo: number; at: number };
  const entries: Entry[] = [
    ...units.map((unit) => ({ kind: 'unit' as const, unit, at: unit.pages[0] ?? 0 })),
    ...skipped.map((pageNo) => ({ kind: 'skipped' as const, pageNo, at: pageNo })),
  ].sort((a, b) => a.at - b.at);

  const emptyNewGroups = newGroups.filter((g) => g.unitKeys.length === 0);
  const emptyAppends = appendGroups.filter((g) => g.unitKeys.length === 0);

  return (
    <div className="two-col">
      <div className="stack">
        <div className="toolbar">
          <span className="small muted">
            {pageCountLabel(data.page_count, data.hidden_page_count)} · {pluralize(units.length, 'slide')} to create ·{' '}
            {skipped.length} skipped
          </span>
          <span className="spacer" />
          <button
            type="button"
            className="btn btn-sm"
            onClick={combine}
            disabled={selected.length < 2}
            title="Combine the selected slides into one slide, pages in page order"
          >
            Combine selected ({selected.length})
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSelected([])} disabled={!selected.length}>
            Clear selection
          </button>
        </div>
        {entries.map((entry) => {
          if (entry.kind === 'skipped') {
            const page = pageByNo.get(entry.pageNo);
            return (
              <div key={`s${entry.pageNo}`} className="import-unit" style={{ opacity: 0.55 }}>
                <div className="import-page">
                  <span className="small faint">{entry.pageNo}</span>
                  <Thumb thumb={page?.thumbnail} alt={`Page ${entry.pageNo}`} hidden={page?.hidden} onRetried={onRefresh} />
                  <div className="stack" style={{ gap: 6 }}>
                    <span className="badge">Skipped</span>
                    <div className="text-excerpt">{page?.text || 'No text on this page.'}</div>
                    <div>
                      <button type="button" className="btn btn-sm" onClick={() => restorePage(entry.pageNo)}>
                        Include page {entry.pageNo}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          const unit = entry.unit;
          const isSelected = selected.includes(unit.key);
          const member = membership(unit.key);
          const titleId = `unit-title-${unit.key}`;
          return (
            <div key={unit.key} className={`import-unit${isSelected ? ' selected' : ''}`}>
              <div className="import-unit-head">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() =>
                      setSelected((s) => (s.includes(unit.key) ? s.filter((k) => k !== unit.key) : [...s, unit.key]))
                    }
                    aria-label={`Select slide with page${unit.pages.length > 1 ? 's' : ''} ${unit.pages.join(', ')}`}
                  />
                </label>
                <label htmlFor={titleId} className="small muted nowrap">
                  Slide title
                </label>
                <input
                  id={titleId}
                  className="input grow"
                  value={unit.title}
                  maxLength={200}
                  placeholder={defaultTitle(pageByNo.get(unit.pages[0] ?? 0))}
                  onChange={(event) =>
                    setUnits((us) => us.map((u) => (u.key === unit.key ? { ...u, title: event.target.value } : u)))
                  }
                />
                {unit.pages.length > 1 ? (
                  <button type="button" className="btn btn-sm" onClick={() => split(unit)}>
                    Split into pages
                  </button>
                ) : null}
              </div>
              {member.length ? (
                <div className="row-wrap" style={{ padding: '0 12px' }}>
                  {member.map((m) => (
                    <span key={m} className="badge badge-accent">
                      {m}
                    </span>
                  ))}
                </div>
              ) : null}
              {unit.pages.map((pageNo) => {
                const page = pageByNo.get(pageNo);
                return (
                  <div key={pageNo} className="import-page">
                    <span className="small faint">{pageNo}</span>
                    <Thumb thumb={page?.thumbnail} alt={`Page ${pageNo}`} hidden={page?.hidden} onRetried={onRefresh} />
                    <div className="stack" style={{ gap: 6 }}>
                      <div className="text-excerpt">{page?.text || 'No text on this page.'}</div>
                      {page?.notes ? (
                        <details className="small">
                          <summary>Speaker notes</summary>
                          <div className="pre muted">{page.notes}</div>
                        </details>
                      ) : null}
                      <div>
                        <button type="button" className="btn btn-sm" onClick={() => skipPage(pageNo)}>
                          Skip page {pageNo}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <aside className="sidebar">
        <section className="panel stack">
          <h3>Groups</h3>
          <p className="small muted">Select slides on the left, then put them into a new or an existing group.</p>
          <div className="field">
            <label htmlFor="new-group-name">New group from selection</label>
            <div className="row">
              <input
                id="new-group-name"
                className="input"
                value={newGroupName}
                maxLength={200}
                placeholder="Group name"
                onChange={(event) => setNewGroupName(event.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm"
                onClick={addNewGroup}
                disabled={!newGroupName.trim() || selected.length === 0}
              >
                Create
              </button>
            </div>
          </div>
          <div className="field">
            <label htmlFor="append-group">Append selection to existing group</label>
            <div className="row">
              <select
                id="append-group"
                className="select"
                value={appendTarget}
                onChange={(event) => setAppendTarget(event.target.value ? Number(event.target.value) : '')}
                disabled={!groups.data}
              >
                <option value="">{groups.data && groups.data.length === 0 ? 'No groups yet' : 'Choose a group…'}</option>
                {(groups.data ?? [])
                  .filter((g) => !g.retired)
                  .map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="btn btn-sm"
                onClick={addAppend}
                disabled={appendTarget === '' || selected.length === 0}
              >
                Append
              </button>
            </div>
            {groups.error ? <ErrorAlert error={groups.error} /> : null}
          </div>
          {newGroups.length || appendGroups.length ? <hr className="divider" /> : null}
          {newGroups.map((g) => (
            <div key={g.key} className="stack" style={{ gap: 4 }}>
              <div className="row-between">
                <strong className="small">New group: {g.name}</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => setNewGroups((gs) => gs.filter((x) => x.key !== g.key))}
                  aria-label={`Remove new group ${g.name}`}
                >
                  Remove
                </button>
              </div>
              <ol className="small muted" style={{ margin: 0, paddingLeft: 20 }}>
                {g.unitKeys.map((k) => (
                  <li key={k}>{unitLabel(k)}</li>
                ))}
              </ol>
            </div>
          ))}
          {appendGroups.map((g) => (
            <div key={g.groupId} className="stack" style={{ gap: 4 }}>
              <div className="row-between">
                <strong className="small">Append to: {groupNames.get(g.groupId) ?? `Group ${g.groupId}`}</strong>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => setAppendGroups((gs) => gs.filter((x) => x.groupId !== g.groupId))}
                  aria-label={`Do not append to ${groupNames.get(g.groupId) ?? g.groupId}`}
                >
                  Remove
                </button>
              </div>
              <ol className="small muted" style={{ margin: 0, paddingLeft: 20 }}>
                {g.unitKeys.map((k) => (
                  <li key={k}>{unitLabel(k)}</li>
                ))}
              </ol>
            </div>
          ))}
          {emptyNewGroups.length || emptyAppends.length ? (
            <div className="alert alert-warning small">
              Groups whose slides were all skipped or combined away are left out on commit.
            </div>
          ) : null}
        </section>

        <section className="panel stack">
          <h3>Commit</h3>
          <p className="small muted">
            Creates {pluralize(units.length, 'slide')}
            {newGroups.length ? `, ${pluralize(newGroups.length, 'new group')}` : ''}
            {appendGroups.length ? `, and appends to ${pluralize(appendGroups.length, 'existing group')}` : ''}. Nothing
            enters the library before you commit.
          </p>
          {tags.length ? (
            <div className="stack" style={{ gap: 4 }}>
              <div className="field-label" id="import-tags-label">
                Tags for the new slides
              </div>
              <div className="row-wrap" role="group" aria-labelledby="import-tags-label">
                {tags.map((tag) => {
                  const on = tagIds.includes(tag.id);
                  return (
                    <label key={tag.id} className="checkbox chip" data-on={on}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={busy}
                        onChange={() => setTagIds((ids) => (on ? ids.filter((id) => id !== tag.id) : [...ids, tag.id]))}
                        aria-label={tag.name}
                      />
                      {tag.name}
                    </label>
                  );
                })}
              </div>
              <span className="field-hint">Every slide this import creates gets the chosen tags. Groups get none.</span>
            </div>
          ) : null}
          <ErrorAlert error={error} />
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void commit()}
            disabled={busy || units.length === 0}
          >
            {busy ? 'Committing…' : 'Commit import'}
          </button>
          {units.length === 0 ? <p className="small muted">Every page is skipped. Include a page or discard the import.</p> : null}
          <button type="button" className="btn" onClick={() => setDiscarding(true)} disabled={busy}>
            Discard import
          </button>
          <p className="xsmall faint">
            Uploaded {formatDateTime(data.created_at)}. An import untouched for 7 days is removed automatically.
          </p>
        </section>
      </aside>

      {discarding ? (
        <TypedConfirmDialog
          title="Discard import"
          name={data.filename}
          what="file name"
          actionLabel="Discard import"
          onClose={() => setDiscarding(false)}
          onConfirm={async (confirm) => {
            await discardImport(data.id, confirm);
            navigate(`/projects/${project.id}/library`);
          }}
        >
          <p>The uploaded deck and its staged pages are deleted. Nothing is added to the library.</p>
        </TypedConfirmDialog>
      ) : null}
    </div>
  );
}

export function ImportPage() {
  const { importId } = useParams();
  const id = Number(importId);
  const imp = useResource(() => getImport(id), [id]);
  const mismatched = useProjectOwnerGuard(imp.data?.project_id);
  const pending = anyPending((imp.data?.pages ?? []).map((p) => p.thumbnail));
  usePolling(pending, () => void imp.refresh());
  useDetailCrumb(imp.data ? `Import: ${imp.data.filename}` : null);

  return (
    <div className="section">
      <DataView resource={imp} what="import" loadingLabel="Loading the import…">
        {(data) => {
          if (mismatched) return <Loading label="Loading the import…" />;
          return (
          <>
            <div className="page-header">
              <div className="page-title">
                <h2>Review import: {data.filename}</h2>
                <p className="muted small">
                  Every page becomes its own slide unless you skip it or combine it with others. Titles left empty use
                  the page’s title.
                </p>
              </div>
            </div>
            {pending ? (
              <div className="alert alert-info small" role="status">
                Page previews are still being rendered. They appear here automatically.
              </div>
            ) : null}
            <ImportReview key={data.id} data={data} onRefresh={() => void imp.refresh()} />
          </>
          );
        }}
      </DataView>
    </div>
  );
}
